/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import { alias, or } from '@ember/object/computed';
import { set, action, computed } from '@ember/object';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import Composer, { BaseComposerArgs } from 'cio/components/action-composer/base-glimmer';
import { EMPTY_BODY_METHODS } from 'cio/utils/composer/webhooks';
import message from 'cio/app-models/action-abstracts/message';
import flattenArray from 'cio/utils/flatten-array';
import getPreviewVariables from 'cio/utils/get-preview-variables';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import { IdentifierKey } from 'cio/utils/identifiers';
import { WebhookPreview } from 'cio/utils/previews';
import AuthService from 'cio/services/auth';

interface ComposerArgs extends BaseComposerArgs {
  preview: WebhookPreview;
}

interface ComposerSignature {
  Args: ComposerArgs;
  Element: HTMLDivElement;
}

export default class Webhook extends Composer<ComposerSignature> {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;

  @alias('validateResponsesAttributesTask.isRunning') isLoadingPreview!: boolean;
  @or('args.editableEntity.template.hasDirtyAttributes', 'actionAbstract.isDirty') isDirty!: boolean;

  @tracked actionAbstract: {
    attributesMappings?: Record<string, string>[] | null;
    [key: string]: any;
  };
  @tracked responsesErrors: any[];

  get liquidInsertAnalyticsData() {
    return {
      campaign: this.args.editableEntity.campaign,
      actionTypeName: 'webhook',
      selectedTab: 'sampleData',
    };
  }

  get shouldShowResponseTab() {
    return this.args.editableEntity.action != null && !this.args.editableEntity.campaign?.isWebhookTriggered;
  }

  @computed('args.preview.errors.[]', 'responsesErrors.[]')
  get _allErrors() {
    let previewErrors = this.args.preview ? this.args.preview.errors : [];
    return {
      ...previewErrors,
      responses: this.responsesErrors ? flattenArray(this.responsesErrors) : [],
    };
  }

  @computed('actionAbstract.attributesMappings.@each.{name,value}')
  get _attributesMappingsFormattedForSendTest() {
    return (this.actionAbstract.attributesMappings ?? []).reduce(
      (aggr, mapping) => ({
        ...aggr,
        [mapping.name]: mapping.value,
      }),
      {},
    );
  }

  @computed('actionAbstract.attributesMappings.@each.name')
  get hasInvalidMappings() {
    return this.actionAbstract.attributesMappings?.some((mapping) => mapping.name === IdentifierKey.CioId);
  }

  previewObserverKeys = [
    'args.editableEntity.template.{url,body,requestMethod}',
    'args.editableEntity.template.headers.@each.{name,value}',
  ];
  previewURL = 'previews/webhook';

  getPreviewData() {
    return {
      url: this.template.url,
      body: this.template.body,
      headers: this.template.headers,
    };
  }

  validateResponsesAttributesTask = task(async (index?: number) => {
    let toValidate = [];

    // validate all response/attributes mappings:
    if (typeof index === 'undefined') {
      toValidate = this.actionAbstract.attributesMappings ?? [];
    } else {
      toValidate = [this.actionAbstract.attributesMappings?.[index]];
    }

    let errors;
    try {
      errors = await Promise.all(
        toValidate.map((mapping: Record<string, any>) => {
          let url = 'previews/attribute';
          let data = {
            name: mapping.name,
            body: mapping.value,
            variables: getPreviewVariables({
              customer: this.args.selectedCustomer ? this.args.selectedCustomer.attrs : {},
              selectedEvent: this.args.selectedEvent,
              templateVariables: this.args.templateVariables,
            }),
          };

          return this.api.post(url, { data }).then((previewResponse) => {
            // ignore "variable "response" is missing" errors: we don't know webhook response shape, but assume it and its properties to be available in this validation
            let relevantLiquidErrors = (previewResponse.errors.body ?? []).filter(
              (e: string) =>
                !/^Variable '(response|response\..*)' is missing$/.test(e) &&
                !e.includes('undefined variable: response,'),
            );

            return relevantLiquidErrors;
          });
        }),
      );
    } catch (e) {
      this.apiErrors.report(e);
    }

    if (typeof index === 'undefined') {
      // all mappings got validated: overwrite all errors
      this.responsesErrors = errors ?? [];
    } else {
      // only one mapping got validated: overwrite specific value
      let newResponseToProfileAttributesMappingsErrors = this.responsesErrors;
      set(newResponseToProfileAttributesMappingsErrors, index, errors?.[0]);
      this.responsesErrors = [...newResponseToProfileAttributesMappingsErrors];
    }
  });

  constructor(owner: unknown, args: ComposerSignature['Args']) {
    super(owner, args);

    this.watchTemplate();

    // attribute mapping is unavailable without an action model
    if (this.args.editableEntity.action) {
      this.actionAbstract = message.create({ action: args.editableEntity.action as any });
      this.responsesErrors = [];
      this.validateResponsesAttributesTask.perform();
    } else {
      this.actionAbstract = {};
      this.responsesErrors = [];
    }
  }

  // The action's name may change due to being updated via the composer's header
  // component. This does not sync to the actionAbstract automatically (and
  // that's a good thing, since actionAbstracts are also used to track name
  // changes in edit VWB edit sidebar, so that makes sense). In this case we
  // need to keep the names in sync, so that it doesn't get overwritten with the
  // old value if we save anything else from the composer itself.
  // https://github.com/customerio/issues/issues/4620
  @action
  didChangeName() {
    this.actionAbstract?.set?.('name', this.args.editableEntity.action?.name);
  }

  @action
  rollback() {
    this.args.editableEntity.template?.rollbackAttributes();
    this.actionAbstract?.prepareForEdit?.();

    this.args.onRollback();
  }

  @action
  save() {
    if (EMPTY_BODY_METHODS.includes(this.template.requestMethod)) {
      this.template.set('body', '');
    }

    this.actionAbstract?.prepareForSave?.();

    return this.args.onSave();
  }

  @action
  onTriggerDataSidebarRowClick(
    openLiquidFallbackModal: (event: Event | null, liquid: string) => unknown,
    value: string,
  ) {
    openLiquidFallbackModal(null, `trigger.${value}`);
  }
}
